import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import DraftFooter from '~/shared-components/DraftFooter';
import { selectHasFirstCommentEntitlement } from '~/organizations/selectors';
import { useShowThreadsUpgrade } from '~/utils/canQueueMoreThreadsForAllTwitterProfiles';
import { usePostActions } from '../../../post/hooks/usePostActions';
import { PostEntity } from '../../../post/PostEntity';
import { usePostPermissions } from '../../../post/hooks';

export const ProfilesDraftFooter = ({
  view,
  draft,
  postAction,
  isDeleting,
  isConfirmingDelete,
  isMoving,
  isWorking,
  onApproveClick,
  onDeleteConfirmClick,
  onEditClick,
  onRevertApprovalClick,
  onRequestApprovalClick,
  onRescheduleClick,
}) => {
  const { isManager, isPostOwner, hasEditPermission } =
    usePostPermissions(draft);

  const hasFirstCommentEntitlement = useSelector(
    selectHasFirstCommentEntitlement
  );

  const firstComment = PostEntity.getFirstComment(draft);
  const isPastDue = PostEntity.isPastDue(draft);
  const isScheduled = PostEntity.isScheduled(draft);

  const isApprovalsTab = view === 'approval';

  // Performing Actions
  const deletingMessage = (isDeleting || isConfirmingDelete) && 'Deleting...';
  const movingMessage = isMoving && isApprovalsTab && 'Moving...';
  const requestingMessage = isMoving && !isApprovalsTab && 'Requesting...';
  const approvingMessage =
    isWorking && isManager && isApprovalsTab && 'Approving...';
  const addingMessage =
    isWorking && isManager && !isApprovalsTab && 'Adding...';

  const isPerformingAction = isDeleting || isMoving || isWorking;
  const actionMessage =
    deletingMessage ||
    movingMessage ||
    requestingMessage ||
    approvingMessage ||
    addingMessage ||
    '';

  const handleEditClick =
    isManager || (isPostOwner && !isApprovalsTab) ? onEditClick : null;
  const onDeleteClick = hasEditPermission ? onDeleteConfirmClick : null;

  const { singleActions, stackedActions } = usePostActions({
    post: draft,
    onApproveClick,
    onRequestApprovalClick,
    onRescheduleClick,
    onRevertApprovalClick,
  });

  const { shouldShowThreadsMigrationModal } = useShowThreadsUpgrade(draft);

  return (
    <DraftFooter
      isScheduled={isScheduled}
      isPastDue={isPastDue}
      description={postAction}
      actionMessage={actionMessage}
      isPerformingAction={!!isPerformingAction}
      hasFirstComment={hasFirstCommentEntitlement && firstComment}
      hasEditPermission={hasEditPermission}
      singleActions={singleActions}
      stackedActions={stackedActions}
      onDeleteClick={onDeleteClick}
      onEditClick={handleEditClick}
      threadCounter={Array.isArray(draft.thread) ? draft.thread.length : 0}
      shouldShowThreadsMigrationModal={shouldShowThreadsMigrationModal}
    />
  );
};

ProfilesDraftFooter.propTypes = {
  view: PropTypes.string.isRequired,
  draft: PropTypes.shape({
    updateType: PropTypes.string,
  }),
  postAction: PropTypes.string,
  isDeleting: PropTypes.bool,
  isConfirmingDelete: PropTypes.bool,
  isMoving: PropTypes.bool,
  isWorking: PropTypes.bool,
  onApproveClick: PropTypes.func,
  onDeleteConfirmClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onRevertApprovalClick: PropTypes.func,
  onRequestApprovalClick: PropTypes.func,
  onRescheduleClick: PropTypes.func,
};

ProfilesDraftFooter.defaultProps = {
  draft: null,
  postAction: null,
  isDeleting: false,
  isMoving: false,
  isWorking: false,
  isConfirmingDelete: false,
  onApproveClick: null,
  onDeleteConfirmClick: null,
  onEditClick: null,
  onRevertApprovalClick: null,
  onRequestApprovalClick: null,
  onRescheduleClick: null,
};
